import { useState, useCallback } from 'react';

const STORAGE_KEY = 'starredAirports';

interface UseStarredAirportsReturn {
  starredAirports: Set<string>;
  isStarred: (code: string) => boolean;
  toggle: (code: string) => void;
  clear: () => void;
}

function loadStarred(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch { /* corrupted — start fresh */ }
  return new Set();
}

function persist(codes: Set<string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(codes)));
}

export function useStarredAirports(): UseStarredAirportsReturn {
  const [starredAirports, setStarredAirports] = useState<Set<string>>(loadStarred);

  const isStarred = useCallback(
    (code: string) => starredAirports.has(code),
    [starredAirports],
  );

  const toggle = useCallback((code: string) => {
    setStarredAirports(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      persist(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    const empty = new Set<string>();
    persist(empty);
    setStarredAirports(empty);
  }, []);

  return { starredAirports, isStarred, toggle, clear };
}
